import {useSyncExternalStore} from 'react';

import {
    createUserMediaHooks,
    getAudioOutput,
    useSelectDeviceChangeHandler,
    useUserMediaContext,
} from '@pexip/media-components';
import {isSendingAudio, isSendingVideo} from '@pexip/infinity';

import {mediaSignals} from '../signals/Media.signals';
import {muteAudio, muteVideo} from '../services/Media.service';
import {config, useConfig} from '../config';
import {combineSubscribes} from '../utils/subscribe';
import {logger} from '../logger';
import {applyCallTypeSendDirectionChange} from '../utils/changeAudioVideoCallTypeDirection';

const subscribeAudioOutputDeviceChanges = combineSubscribes(
    mediaSignals.onDevicesChanged.add,
    onChange => config.subscribe('audioOutput', onChange),
);

export const {
    useAudioInput,
    useAudioInputUnavailable,
    useAudioMuteState,
    useAudioSuspended,
    useDevices,
    useHaveRequestedAudio,
    useHaveRequestedMedia,
    useHaveRequestedVideo,
    useInputDeviceStatusInfo,
    useMediaStatus,
    useMediaStream,
    useMuteAudio,
    useMuteVideo,
    useSourceAudioTrack,
    useSourceVideoTrack,
    useToggleAudioMuted,
    useToggleVideoMuted,
    useVideoInput,
    useVideoInputUnavailable,
    useVideoMuteState,
    useVideoSuspended,
} = createUserMediaHooks({signals: mediaSignals, muteAudio, muteVideo});

export const useVideoInputChangeHandler = () => {
    const userMedia = useUserMediaContext();
    const devices = useDevices();
    const selectedVideoInput = useVideoInput();

    return useSelectDeviceChangeHandler({
        currentInput: selectedVideoInput,
        devices,
        requestMedia: device => {
            if (!device) {
                return;
            }
            const constraints = {
                video: device.deviceId ? {device: {exact: device}} : true,
            };
            applyCallTypeSendDirectionChange(constraints);
            if (!isSendingVideo(config.get('callType'))) {
                return;
            }
            if (device.deviceId) {
                logger.info(constraints, 'Change VideoInput');
                config.set({
                    key: 'videoInput',
                    value: device,
                    persist: true,
                });
            }
            userMedia.getUserMedia(constraints);
        },
    });
};

export const useAudioInputChangeHandler = () => {
    const userMedia = useUserMediaContext();
    const devices = useDevices();
    const selectedAudioInput = useAudioInput();

    return useSelectDeviceChangeHandler({
        currentInput: selectedAudioInput,
        devices,
        requestMedia: device => {
            if (!device) {
                return;
            }
            const constraints = {
                audio: device.deviceId ? {device: {exact: device}} : true,
            };
            applyCallTypeSendDirectionChange(constraints);
            if (!isSendingAudio(config.get('callType'))) {
                return;
            }
            if (device.deviceId) {
                logger.info(constraints, 'Change AudioInput');
                config.set({
                    key: 'audioInput',
                    value: device,
                    persist: true,
                });
            }
            userMedia.getUserMedia(constraints);
        },
    });
};

export const useAudioOutput = () => {
    const userMedia = useUserMediaContext();

    return useSyncExternalStore(subscribeAudioOutputDeviceChanges, () => {
        return getAudioOutput(userMedia.devices, config.get('audioOutput'));
    });
};

export const useAudioOutputChangeHandler = (persist: boolean) => {
    const audioOutput = useAudioOutput();
    const devices = useDevices();

    return useSelectDeviceChangeHandler({
        currentInput: audioOutput,
        devices,
        requestMedia: device => {
            if (!device) {
                return;
            }
            logger.info({device, persist}, 'Change AudioOutput');
            config.set({key: 'audioOutput', value: device, persist});
        },
    });
};

export const useMediaRequest = () => {
    const [callType] = useConfig('callType');
    const requestAudio = isSendingAudio(callType);
    const requestVideo = isSendingVideo(callType);
    return {audio: requestAudio, video: requestVideo};
};

export const useUpdatingAudio = (
    subscribe = mediaSignals.onUpdatingAudio.add,
) => {
    const userMedia = useUserMediaContext();
    return useSyncExternalStore(subscribe, () => userMedia.updatingAudio);
};
export const useUpdatingVideo = (
    subscribe = mediaSignals.onUpdatingVideo.add,
) => {
    const userMedia = useUserMediaContext();
    return useSyncExternalStore(subscribe, () => userMedia.updatingVideo);
};
